/**
 * useWalkthroughStep — one-call replacement for the repeated
 *   "register target + set overlay proxy" pattern.
 *
 * Usage:
 *   const { viewRef } = useWalkthroughStep(WALKTHROUGH_STEPS.CREATE_BTN, {
 *     onProxyTap: handlePress,   // called when user taps the spotlight
 *   })
 *   // attach viewRef to the View that should be spotlighted
 */

import { useEffect } from 'react'
import { useWalkthroughTarget } from './useWalkthroughTarget'
import { useGuestWalkthroughStore } from '../stores/guestWalkthroughStore'
import { afterLayout } from '../utils/afterLayout'
import type { WalkthroughStepKey } from '../../create/types/guestWalkthrough.types'
import type React from 'react'
import type { View } from 'react-native'

interface UseWalkthroughStepOptions {
  /** Called when the user taps the spotlight proxy. If null/undefined, no proxy is shown. */
  onProxyTap?: (() => void) | null
  /** Use double-RAF (afterLayout) before measuring — needed when the target may not be laid out yet. */
  useAfterLayout?: boolean
  /** Gate registration/proxy wiring until prerequisite UI/state is ready. */
  isEnabled?: boolean
}

interface UseWalkthroughStepReturn {
  viewRef: React.RefObject<View | null>
}

export function useWalkthroughStep(
  stepKey: WalkthroughStepKey,
  options: UseWalkthroughStepOptions = {}
): UseWalkthroughStepReturn {
  const { onProxyTap = null, useAfterLayout: needsAfterLayout = false, isEnabled = true } = options
  const { viewRef, registerTarget } = useWalkthroughTarget(stepKey)
  const isActive = useGuestWalkthroughStore((s) => s.isActive)
  const currentStep = useGuestWalkthroughStore((s) => s.currentStep)
  const setOverlayProxyAction = useGuestWalkthroughStore((s) => s.setOverlayProxyAction)

  useEffect(() => {
    if (!isEnabled || !isActive || currentStep !== stepKey) return
    if (needsAfterLayout) {
      afterLayout(registerTarget)
    } else {
      registerTarget()
    }
    if (!onProxyTap) return
    setOverlayProxyAction(onProxyTap)
    return () => setOverlayProxyAction(null)
  // registerTarget and setOverlayProxyAction are stable references
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnabled, isActive, currentStep, stepKey, onProxyTap, needsAfterLayout])

  return { viewRef }
}
